import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import { PageActions } from "./PageActions";

const copy = {
  en: {
    eyebrow: "Instructions",
    title: "Use SailPlot with confidence.",
    intro:
      "This guide takes you from a blank board to a finished sailing explanation that you can edit, save, share, and export.",
    askTitle: "Need help with SailPlot?",
    askText:
      "Open ChatGPT and describe what you want to create or where you are stuck. Mention SailPlot and the tool or function you are using for a more specific answer.",
    askAction: "Ask ChatGPT",
    askPrompt:
      "Read the SailPlot instructions at {instructionsUrl} and help me use the editor. Reply in English.\n\nMy Question:\n\n",
    askPrivacy:
      "Only the public instructions link and this prepared question are passed to ChatGPT. No plot or browser data is included.",
    quickEyebrow: "Quick start",
    quickTitle: "Your first plot.",
    quickLabel: "SailPlot quick start",
    quickSteps: [
      [
        "Open a project",
        "Open Projects to start with a blank board, use a template, reopen a local project, or import a SailPlot JSON file.",
      ],
      [
        "Build the situation",
        "Choose a tool, then place boats, marks, lines, arrows, shapes, text, and notes on the board.",
      ],
      [
        "Refine the explanation",
        "Select an object and use Properties to change its course, appearance, label, zone, sails, or other details.",
      ],
      [
        "Share or export",
        "Send an editable URL or QR code, download a project backup, or create a PNG, GIF, MP4, or PDF.",
      ],
    ],
    guideEyebrow: "Detailed guide",
    guideTitle: "Everything in the editor.",
    guideText:
      "The controls contain the same core functions on desktop, tablet, and phone. In the compact layout, tools are at the bottom and Properties opens above the board.",
    guideLabel: "Detailed SailPlot instructions",
    sections: [
      {
        title: "Projects",
        description:
          "Use Projects to create, reopen, organize, and transfer your sailing boards.",
        items: [
          "Start with a blank plot or choose a prepared template for a common sailing situation.",
          "Recent projects are stored locally in this browser. Open, duplicate, or delete them from the project overview.",
          "Import a SailPlot JSON file to continue a board from another browser or device.",
          "Give the plot a clear title. Plot details also support a description, racing-rule references, wind strength, and additional information.",
        ],
      },
      {
        title: "Tools",
        description:
          "Choose a tool first, then tap, click, or drag on the board to add an object.",
        items: [
          "Boat stays active. Every placement adds the next numbered position to the current boat chain. Select a boat from an existing chain before choosing Boat to continue that sequence.",
          "Mark and Lee mark create individual buoys. Gate, Start line, and Finish line create complete course elements with two ends.",
          "Line, Arrow, Rectangle, and Circle can be dragged directly or placed with two clicks. Freehand follows the pointer, finger, or stylus. Text is placed with a click or tap.",
          "Select returns to object editing. Pan moves the view without moving objects.",
        ],
      },
      {
        title: "Player",
        description:
          "Replay numbered boat positions without changing the editable plot.",
        items: [
          "Player mode is available as soon as at least one boat chain contains two positions.",
          "On wide screens, the player controls replace the left tool panel and the Properties panel is hidden. Compact screens use the bottom control row.",
          "On phones, Player sits beside the menu, language selection is inside that menu, and the red X in the player bar returns to the editor.",
          "Play or pause the movement, jump to the previous or next position, or drag the timeline in either direction.",
          "Set playback speed to 0.5×, 1×, or 2× and show or hide the travelled boat tails directly in the player controls.",
          "Choose Export animation in the Player tools to download GIF, transparent GIF, or MP4. The file uses the currently selected speed and boat-tail setting.",
          "Overlap lines remain visible on the animated boats whenever they are configured for the current position.",
          "Boats follow the curved route between positions and turn with its direction instead of sliding sideways.",
          "Every transition between two numbered positions takes the same amount of time. Shorter boat chains remain at their final position.",
          "Choose Back to editor to restore the tools and Properties without changing the stored positions.",
        ],
      },
      {
        title: "Select and edit",
        description:
          "Select an object on the board to open all available settings in Properties.",
        items: [
          "Drag selected objects to move them. Use the rotation handle where available, or enter exact values in Properties.",
          "Boat properties include class, name, sail number, heading, tack, hull color, sails, sail trim, and fixed position.",
          "The grey Umpire boat uses the Coachboat hull. Coachboats and Umpire boats can show a boat-identification flag using a racing-boat color from the current plot together with an independent protest, penalty, no-penalty, yellow, blue, or disqualification signal.",
          "Sailing boats can carry a red protest flag on the left or right stern corner. Choose Yes left, No, or Yes right in Properties; support boats do not use this control.",
          "Visible position numbers are placed on the hull beneath the sails. Sail fills and outlines remain clearly distinguishable on both light and dark plots.",
          "Mark and course-element properties include number, type, shape, color, zone, orientation, endpoints, laylines, and labels where applicable.",
          "New marks use Neutral orientation and show no mark laylines. Choose Windward or Leeward to add laylines in the corresponding direction.",
          "Lines, arrows, text, and shapes provide stroke, fill, opacity, font, and size controls according to the selected object. Empty circles and rectangles are selected from their borders so objects underneath remain clickable; filled shapes use their complete area. Drag the single handle at the top of a selected circle to change its radius.",
          "Shift-click adds objects to a multi-selection. Move, duplicate, lock, change layer order, or delete the selection together.",
        ],
      },
      {
        title: "Scene",
        description:
          "Scene settings control the shared environment and presentation of the whole plot.",
        items: [
          "Show or hide the grid, wind indicator, laylines, mark zones, boat numbers, and boat legend. The boat legend is off by default.",
          "Grid spacing is always 1 BL and follows the selected boat-length basis; visibility remains adjustable.",
          "Set the wind direction so boats, laylines, and course elements relate to the intended conditions.",
          "Enable Endless plot to keep drawing in every direction without fixed plot edges. In this mode, drag the wind indicator and boat legend to place them freely. Its Home button returns the view to the central starting position; keep the bounded plot for a defined page format.",
          "Choose a light or dark plot background and select the boat class used as the boat-length basis for zones and measurements.",
        ],
      },
      {
        title: "Navigate",
        description:
          "Move quickly around larger plots without disturbing the sailing situation.",
        items: [
          "Scroll to zoom. Endless plot supports zooming out to 10%. Choose Pan or hold Space while dragging to move the view.",
          "Use Fit canvas to bring the complete situation back into view.",
          "Use the arrow keys for small object movements and Delete to remove a selection.",
          "Use Cmd/Ctrl+Z to undo, Cmd/Ctrl+Shift+Z to redo, and Cmd/Ctrl+D to duplicate selected objects.",
          "On compact screens, scroll through the bottom tool row and open Properties after selecting an object.",
        ],
      },
      {
        title: "Save locally",
        description: "SailPlot is local-first and does not require an account.",
        items: [
          "Changes and preferences are stored automatically in the current browser using IndexedDB.",
          "Local projects do not automatically appear in another browser, user profile, or device.",
          "Download a Plot JSON file for a durable backup or to transfer the complete editable project.",
          "Clearing browser site data can remove locally stored projects, so keep JSON backups of important work.",
        ],
      },
      {
        title: "Share link",
        description:
          "A share link sends a complete editable copy without uploading the project to SailPlot.",
        items: [
          "Open Export & Share and choose Copy URL with project.",
          "The compressed project data is stored directly in the URL fragment. Anyone with the link can open the embedded plot.",
          "The recipient receives an editable copy and can move objects, continue the explanation, save it locally, or export it.",
          "Very detailed plots can create long URLs that some messaging apps shorten. Use Plot JSON when SailPlot displays a length warning.",
        ],
      },
      {
        title: "QR code",
        description:
          "The plot QR code opens the same editable project as the share link.",
        items: [
          "Click or tap the QR code on the board to enlarge it for scanning or to duplicate the plot into a new tab.",
          "The QR code updates with the current plot because it contains the current share URL.",
          "PNG, GIF, MP4, and PDF outputs include a plot QR code so an export can reopen the editable board.",
          "If a project becomes too large for a reliable QR code, use the JSON project file to transfer it.",
        ],
      },
      {
        title: "Export",
        description:
          "Open Export & Share and choose the format that matches what you want to do next.",
        items: [
          "Plot JSON preserves the complete editable project and is the best format for backups and later editing.",
          "PNG creates one high-quality static image without editor handles. Transparent PNG removes the plot background while keeping the plot objects, branding, and QR code. With Endless plot, both include all content outside the original plot area and preserve a clean margin around a freely placed boat legend.",
          "The Dynamic exports note opens the Player tools. There, GIF exports the complete sequence as a loop and is also available with a transparent plot background; MP4 creates a smoother high-quality video with the plot background.",
          "PDF creates an A4 landscape document with a clickable plot QR watermark for bounded plots. It is disabled while Endless plot is active.",
          "All project, image, animation, video, and PDF exports are generated locally on your device.",
        ],
      },
    ],
    noteTitle: "Privacy and access",
    note: "SailPlot does not upload project data to a SailPlot server. A share URL contains the project itself, so anyone who receives that URL can access the embedded plot. Only share it with the people who should see the situation.",
  },
  de: {
    eyebrow: "Anleitung",
    title: "SailPlot sicher bedienen.",
    intro:
      "Diese Anleitung führt dich vom leeren Board bis zur fertigen Segelerklärung, die du bearbeiten, sichern, teilen und exportieren kannst.",
    askTitle: "Brauchst du Hilfe mit SailPlot?",
    askText:
      "Öffne ChatGPT und beschreibe, was du erstellen möchtest oder an welcher Stelle du nicht weiterkommst. Nenne SailPlot und das verwendete Werkzeug oder die Funktion für eine konkretere Antwort.",
    askAction: "ChatGPT fragen",
    askPrompt:
      "Lies die SailPlot-Anleitung unter {instructionsUrl} und hilf mir bei der Bedienung des Editors. Antworte auf Deutsch.\n\nMeine Frage:\n\n",
    askPrivacy:
      "Nur der öffentliche Link zur Anleitung und diese vorbereitete Frage werden an ChatGPT übergeben. Plot- oder Browserdaten sind nicht enthalten.",
    quickEyebrow: "Schnellstart",
    quickTitle: "Dein erster Plot.",
    quickLabel: "SailPlot-Schnellstart",
    quickSteps: [
      [
        "Projekt öffnen",
        "Öffne Projekte, um mit einem leeren Board oder einer Vorlage zu starten, ein lokales Projekt zu öffnen oder eine SailPlot-JSON-Datei zu importieren.",
      ],
      [
        "Situation aufbauen",
        "Wähle ein Werkzeug und platziere Boote, Tonnen, Linien, Pfeile, Formen, Text und Notizen auf dem Board.",
      ],
      [
        "Erklärung verfeinern",
        "Wähle ein Objekt aus und ändere in Eigenschaften den Kurs, die Darstellung, Beschriftung, Zone, Segel oder weitere Details.",
      ],
      [
        "Teilen oder exportieren",
        "Sende eine editierbare URL oder einen QR-Code, lade eine Projektsicherung herunter oder erstelle PNG, GIF, MP4 oder PDF.",
      ],
    ],
    guideEyebrow: "Ausführliche Anleitung",
    guideTitle: "Alles im Editor.",
    guideText:
      "Die wichtigsten Funktionen stehen auf Desktop, Tablet und Smartphone zur Verfügung. In der kompakten Ansicht liegen die Werkzeuge unten und die Eigenschaften öffnen sich über dem Board.",
    guideLabel: "Ausführliche SailPlot-Anleitung",
    sections: [
      {
        title: "Projekte",
        description:
          "Unter Projekte kannst du deine Segelboards erstellen, öffnen, organisieren und übertragen.",
        items: [
          "Beginne mit einem leeren Plot oder wähle eine vorbereitete Vorlage für eine typische Segelsituation.",
          "Die letzten Projekte werden lokal in diesem Browser gespeichert. In der Projektübersicht kannst du sie öffnen, duplizieren oder löschen.",
          "Importiere eine SailPlot-JSON-Datei, um ein Board aus einem anderen Browser oder von einem anderen Gerät weiterzubearbeiten.",
          "Vergib einen klaren Titel. In den Plot-Details kannst du zusätzlich Beschreibung, Regattaregel-Verweise, Windstärke und weitere Informationen hinterlegen.",
        ],
      },
      {
        title: "Werkzeuge",
        description:
          "Wähle zuerst ein Werkzeug und füge das Objekt danach per Tippen, Klicken oder Ziehen hinzu.",
        items: [
          "Das Boot-Werkzeug bleibt aktiv. Jede Platzierung ergänzt die nächste nummerierte Position der aktuellen Bootskette. Wähle ein Boot einer vorhandenen Kette aus und danach Boot, um diese Sequenz fortzusetzen.",
          "Marke und Leemarke erstellen einzelne Tonnen. Gate, Startlinie und Ziellinie erstellen vollständige Bahnelemente mit zwei Enden.",
          "Linie, Pfeil, Rechteck und Kreis kannst du direkt aufziehen oder mit zwei Klicks platzieren. Freihand folgt Maus, Finger oder Stift. Text wird mit einem Klick oder Tippen gesetzt.",
          "Auswählen kehrt zur Objektbearbeitung zurück. Verschieben bewegt die Ansicht, ohne Objekte zu verändern.",
        ],
      },
      {
        title: "Player",
        description:
          "Gib nummerierte Bootspositionen wieder, ohne den editierbaren Plot zu verändern.",
        items: [
          "Der Player-Modus steht zur Verfügung, sobald mindestens eine Bootskette zwei Positionen enthält.",
          "Auf breiten Bildschirmen ersetzt die Player-Steuerung die linke Werkzeugleiste und der Bereich Eigenschaften wird ausgeblendet. Kompakte Bildschirme verwenden die untere Steuerungsleiste.",
          "Auf Smartphones befindet sich der Player direkt neben dem Menü, die Sprachauswahl liegt im Menü und das rote X in der Player-Leiste führt zurück zum Editor.",
          "Starte oder pausiere die Bewegung, springe zur vorherigen oder nächsten Position oder bewege die Zeitleiste in beide Richtungen.",
          "Stelle die Wiedergabegeschwindigkeit direkt in der Player-Steuerung auf 0,5×, 1× oder 2× und blende Bootsspuren ein oder aus.",
          "Wähle in den Player-Werkzeugen Animation exportieren, um GIF, transparentes GIF oder MP4 herunterzuladen. Die Datei übernimmt die aktuell gewählte Geschwindigkeit und Bootsspuren-Einstellung.",
          "Overlap-Linien bleiben an den animierten Booten sichtbar, wenn sie für die aktuelle Position konfiguriert sind.",
          "Boote folgen der gekrümmten Route zwischen den Positionen und drehen sich mit deren Richtung, statt seitlich zu driften.",
          "Jeder Übergang zwischen zwei nummerierten Positionen dauert gleich lang. Kürzere Bootsketten bleiben an ihrer letzten Position stehen.",
          "Mit Zurück zum Editor stellst du Werkzeuge und Eigenschaften wieder her, ohne gespeicherte Positionen zu verändern.",
        ],
      },
      {
        title: "Auswählen und bearbeiten",
        description:
          "Wähle ein Objekt auf dem Board aus, um alle verfügbaren Einstellungen unter Eigenschaften zu öffnen.",
        items: [
          "Ziehe ausgewählte Objekte, um sie zu verschieben. Nutze, falls vorhanden, den Drehpunkt oder gib genaue Werte in den Eigenschaften ein.",
          "Bei Booten kannst du unter anderem Klasse, Name, Segelnummer, Kurs, Bug, Rumpffarbe, Segel, Segeltrimm und feste Position einstellen.",
          "Das graue Umpireboot verwendet den Coachboat-Rumpf. Coachboats und Umpireboote können gleichzeitig eine Bootskennflagge in einer Regattabootfarbe des aktuellen Plots und ein unabhängiges Protest-, Straf-, Keine-Strafe-, gelbes, blaues oder Disqualifikationssignal zeigen.",
          "Segelboote können eine rote Protestflagge an der linken oder rechten Heckecke führen. Wähle in den Eigenschaften Ja links, Nein oder Ja rechts; Supportboote verwenden diese Steuerung nicht.",
          "Sichtbare Positionsnummern liegen auf dem Rumpf unter den Segeln. Segelflächen und Konturen bleiben auf hellen und dunklen Plots klar unterscheidbar.",
          "Bei Tonnen und Bahnelementen kannst du je nach Objekt Nummer, Typ, Form, Farbe, Zone, Ausrichtung, Endpunkte, Laylines und Beschriftung ändern.",
          "Neue Tonnen verwenden die Ausrichtung Neutral und zeigen keine Tonnen-Laylines. Wähle Luv oder Lee, um Laylines in der entsprechenden Richtung einzublenden.",
          "Linien, Pfeile, Texte und Formen bieten passend zum Objekt Einstellungen für Kontur, Füllung, Deckkraft, Schrift und Grösse. Ungefüllte Kreise und Rechtecke werden am Rand ausgewählt, damit darunterliegende Objekte anklickbar bleiben; gefüllte Formen nutzen ihre ganze Fläche. Ziehe den einzelnen Griff oben am ausgewählten Kreis, um seinen Radius zu ändern.",
          "Mit Umschalt-Klick ergänzt du weitere Objekte zur Auswahl. Du kannst die gesamte Auswahl verschieben, duplizieren, sperren, in den Ebenen anordnen oder löschen.",
        ],
      },
      {
        title: "Szene",
        description:
          "Die Szenen-Einstellungen steuern die gemeinsame Umgebung und Darstellung des gesamten Plots.",
        items: [
          "Blende Raster, Windanzeige, Laylines, Tonnenzonen, Bootsnummern und Bootslegende ein oder aus. Die Bootslegende ist standardmässig ausgeschaltet.",
          "Der Rasterabstand beträgt immer 1 BL und folgt der gewählten Bootslängen-Basis; die Sichtbarkeit bleibt einstellbar.",
          "Stelle die Windrichtung ein, damit Boote, Laylines und Bahnelemente zu den geplanten Bedingungen passen.",
          "Aktiviere Endlos-Plot, um ohne feste Plot-Ränder in alle Richtungen weiterzuzeichnen. In diesem Modus kannst du Windanzeige und Bootslegende frei verschieben. Der Home-Button führt die Ansicht zur zentralen Ausgangsposition zurück; für ein definiertes Seitenformat bleibt der begrenzte Plot verfügbar.",
          "Wähle einen hellen oder dunklen Plot-Hintergrund und die Bootsklasse, die als Bootslängen-Basis für Zonen und Messungen dient.",
        ],
      },
      {
        title: "Navigieren",
        description:
          "Bewege dich schnell durch grössere Plots, ohne die Segelsituation zu verändern.",
        items: [
          "Scrolle zum Zoomen; im Endlos-Plot kannst du bis auf 10 % herauszoomen. Wähle Verschieben oder halte beim Ziehen die Leertaste gedrückt, um den sichtbaren Ausschnitt zu bewegen.",
          "Mit Zeichenfläche einpassen bringst du die vollständige Situation wieder in den sichtbaren Bereich.",
          "Nutze die Pfeiltasten für kleine Objektbewegungen und Löschen zum Entfernen der Auswahl.",
          "Mit Cmd/Strg+Z machst du Änderungen rückgängig, mit Cmd/Strg+Umschalt+Z stellst du sie wieder her und mit Cmd/Strg+D duplizierst du ausgewählte Objekte.",
          "Auf kompakten Bildschirmen kannst du durch die untere Werkzeugleiste scrollen und nach der Objektauswahl die Eigenschaften öffnen.",
        ],
      },
      {
        title: "Lokal speichern",
        description: "SailPlot arbeitet lokal und benötigt kein Benutzerkonto.",
        items: [
          "Änderungen und Einstellungen werden automatisch mit IndexedDB im aktuellen Browser gespeichert.",
          "Lokale Projekte erscheinen nicht automatisch in einem anderen Browser, Benutzerprofil oder auf einem anderen Gerät.",
          "Lade eine Plot-JSON-Datei als dauerhafte Sicherung oder zur Übertragung des vollständigen editierbaren Projekts herunter.",
          "Beim Löschen der Browser-Websitedaten können lokale Projekte verloren gehen. Sichere wichtige Arbeiten deshalb als JSON-Datei.",
        ],
      },
      {
        title: "Freigabelink",
        description:
          "Ein Freigabelink übermittelt eine vollständige editierbare Kopie, ohne das Projekt zu SailPlot hochzuladen.",
        items: [
          "Öffne Exportieren & Teilen und wähle URL mit Projekt kopieren.",
          "Die komprimierten Projektdaten liegen direkt im URL-Fragment. Alle Personen mit diesem Link können den eingebetteten Plot öffnen.",
          "Empfänger erhalten eine editierbare Kopie und können Objekte verschieben, die Erklärung fortsetzen, sie lokal speichern oder exportieren.",
          "Sehr detaillierte Plots können lange URLs erzeugen, die manche Messenger kürzen. Nutze Plot-JSON, wenn SailPlot eine Längenwarnung zeigt.",
        ],
      },
      {
        title: "QR-Code",
        description:
          "Der Plot-QR-Code öffnet dasselbe editierbare Projekt wie der Freigabelink.",
        items: [
          "Klicke oder tippe auf den QR-Code am Board, um ihn zum Scannen zu vergrössern oder den Plot in einem neuen Tab zu duplizieren.",
          "Der QR-Code aktualisiert sich mit dem aktuellen Plot, weil er die jeweils aktuelle Freigabe-URL enthält.",
          "PNG, GIF, MP4 und PDF enthalten einen Plot-QR-Code, damit ein Export das editierbare Board wieder öffnen kann.",
          "Wird ein Projekt für einen zuverlässigen QR-Code zu gross, überträgst du es mit der JSON-Projektdatei.",
        ],
      },
      {
        title: "Export",
        description:
          "Öffne Exportieren & Teilen und wähle das Format passend zur nächsten Verwendung.",
        items: [
          "Plot-JSON erhält das vollständige editierbare Projekt und eignet sich am besten für Sicherungen und spätere Bearbeitung.",
          "PNG erstellt ein hochwertiges statisches Bild ohne Editor-Griffe. Transparentes PNG entfernt den Plot-Hintergrund und behält Plot-Objekte, Branding und QR-Code. Im Endlos-Plot nehmen beide Formate sämtliche Inhalte ausserhalb der ursprünglichen Fläche auf und bewahren einen sauberen Rand um eine frei platzierte Bootslegende.",
          "Der Hinweis Dynamische Exporte öffnet die Player-Werkzeuge. Dort exportiert GIF den vollständigen Ablauf als Schleife und ist zusätzlich mit transparentem Plot-Hintergrund verfügbar; MP4 erzeugt ein flüssigeres, hochwertiges Video mit Plot-Hintergrund.",
          "PDF erstellt für begrenzte Plots ein Dokument im A4-Querformat mit klickbarem Plot-QR-Wasserzeichen. Bei aktivem Endlos-Plot ist der Export deaktiviert.",
          "Alle Projekt-, Bild-, Animations-, Video- und PDF-Exporte werden lokal auf deinem Gerät erzeugt.",
        ],
      },
    ],
    noteTitle: "Datenschutz und Zugriff",
    note: "SailPlot lädt keine Projektdaten auf einen SailPlot-Server. Eine Freigabe-URL enthält das Projekt selbst. Deshalb können alle Personen mit dieser URL den eingebetteten Plot öffnen. Teile sie nur mit Personen, die die Situation sehen dürfen.",
  },
} as const;

export function InstructionsPage({
  language,
  navigate,
}: SailPlotExtensionContext) {
  const text = copy[language];
  const instructionsUrl = new URL(
    "/instructions",
    window.location.origin,
  ).toString();
  const askChatGptUrl = `https://chatgpt.com/?${new URLSearchParams({
    prompt: text.askPrompt.replace("{instructionsUrl}", instructionsUrl),
  }).toString()}`;

  return (
    <article className="platform-page platform-reference-page platform-instructions-page">
      <header>
        <span className="platform-page-eyebrow">{text.eyebrow}</span>
        <h1>{text.title}</h1>
        <p>{text.intro}</p>
      </header>

      <aside className="platform-instructions-chatgpt">
        <div>
          <strong>{text.askTitle}</strong>
          <p>{text.askText}</p>
          <small>{text.askPrivacy}</small>
        </div>
        <a
          href={askChatGptUrl}
          className="customize-button customize-button--primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          {text.askAction} ↗
        </a>
      </aside>

      <section className="platform-instructions-quick-start">
        <header>
          <span className="platform-page-eyebrow">{text.quickEyebrow}</span>
          <h2>{text.quickTitle}</h2>
        </header>
        <ol className="platform-instruction-grid" aria-label={text.quickLabel}>
          {text.quickSteps.map(([title, description], index) => (
            <li key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{title}</strong>
              <p>{description}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="platform-instructions-manual">
        <header>
          <span className="platform-page-eyebrow">{text.guideEyebrow}</span>
          <h2>{text.guideTitle}</h2>
          <p>{text.guideText}</p>
        </header>
        <div
          className="platform-instruction-sections"
          aria-label={text.guideLabel}
        >
          {text.sections.map((section, index) => (
            <section
              key={section.title}
              className="platform-instruction-section"
            >
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{section.title}</h3>
              <p>{section.description}</p>
              <ul>
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </section>

      <aside className="platform-page-note">
        <strong>{text.noteTitle}</strong>
        <p>{text.note}</p>
      </aside>
      <PageActions language={language} navigate={navigate} />
    </article>
  );
}
